import { UserInvitationOptionalResponseDto } from "@/lib/interfaces/invitation";
import { useContext, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import useApiRequest from "../../../lib/hooks/useRequest";
import { Button } from "../../../ui/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";
import { Context, ErrorEntity } from "../../root/context";
import { OnboardingFormData } from "../onboarding-schema";

interface CompanyDataStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const CompanyDataStep = ({ form }: CompanyDataStepProps) => {
  const { apiRequest } = useApiRequest();
  const { setError } = useContext(Context);
  const [inviteCheckIsLoading, setInviteCheckIsLoading] = useState(false);
  const canCheckInvites = useMemo(() => Boolean(setError), [setError]);

  const handleCheckInvitation = async () => {
    if (!setError) return;
    setInviteCheckIsLoading(true);
    const res = await apiRequest<UserInvitationOptionalResponseDto>("organizations/invitations", "GET");
    setInviteCheckIsLoading(false);

    if (res.data?.invitation) {
      setError({
        entity: ErrorEntity.CheckInvite,
        error: "CheckInvite",
        message: "CheckInvite",
        statusCode: 200,
      });
      return;
    }

    toast.info("Keine Einladung gefunden");
  };

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 2 von 9
      </p>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Unternehmensdaten & Rechnungsadresse</h1>
        <Button
          type="button"
          disabled={!canCheckInvites || inviteCheckIsLoading}
          onClick={handleCheckInvitation}
          className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
        >
          {inviteCheckIsLoading ? "Prüfe..." : "Ich habe eine Einladung"}
        </Button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Diese Daten werden für die Rechnungsstellung und den Vertrag benötigt.
      </p>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Unternehmensname <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="z.B. Gesundheitsunternehmen XY GmbH" className="mt-1" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ceo"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Geschäftsführer <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Vor- und Nachname" className="mt-1" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border-t border-gray-100 pt-4 mt-4" />

          <FormField
            control={form.control}
            name="street"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Straße & Hausnummer <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="z.B. Zürichstrasse 3" className="mt-1" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="postCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    PLZ <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z.B. 8000" className="mt-1" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="placeName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Ort <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z.B. Zürich" className="mt-1" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDataStep;
